// ============================================================================
// Beacon — the negotiation worksheet. Turns the due-diligence findings + public
// records into a quantified, itemized reduction, then anchors your revised offer:
//
//   revised offer = MIN( asking − documented defects/liens ,  max-profit price )
//
// So the number is BOTH justified by what you found AND capped at what still
// pays you. Every line is backup for the AI offer letter (layer 3).
// ============================================================================

import type { Deal } from "../types";
import { calc } from "../engine/engine";
import { withDiligence, negotiableDefects } from "./diligence";
import { maxPriceForReturn } from "./maxprice";

export const DEFAULT_TARGET_CAP = 0.065;

export interface ReductionLine {
  label: string;
  amount: number;
  source: string;
  severity?: string;
}

export interface Negotiation {
  asking: number; // the price on the table (deal.purchase)
  reductions: ReductionLine[]; // itemized, backed-up deductions
  totalReduction: number;
  reducedPrice: number; // asking − reductions
  maxProfitPrice: number; // highest price that still clears your targets
  maxFeasible: boolean; // false when no price clears the targets
  revisedOffer: number; // MIN(reducedPrice, maxProfitPrice)
  binding: "defects" | "profit" | "asking"; // what set the number
  discount: number; // (asking − revisedOffer) / asking
  capAtOffer: number; // projected cap-on-cost at the revised offer
  dscrAtOffer: number; // projected DSCR at the revised offer
  targetCap: number;
}

export function buildNegotiation(deal: Deal, opts?: { targetCap?: number }): Negotiation {
  const targetCap = opts?.targetCap ?? DEFAULT_TARGET_CAP;
  const asking = deal.purchase;

  // 1) documented reductions — inspection defects (with cost) + title liens
  const defects = negotiableDefects(withDiligence(deal));
  const reductions: ReductionLine[] = defects.map((d) => ({
    label: `${d.system}: ${d.label}`,
    amount: d.cost ?? 0,
    source: d.source || "inspection",
    severity: d.severity,
  }));
  if ((deal.openLiens ?? 0) > 0) {
    reductions.push({ label: "Open liens / judgments (title)", amount: deal.openLiens, source: "public records" });
  }
  const totalReduction = reductions.reduce((s, r) => s + r.amount, 0);
  const reducedPrice = Math.max(0, asking - totalReduction);

  // 2) profit ceiling — highest price still clearing your targets
  const mp = maxPriceForReturn(deal, { minCap: targetCap, minDscr: deal.dscrMin });
  const profitCeiling = mp.feasible ? mp.price : reducedPrice; // infeasible → don't let it raise the offer

  // 3) anchor: the lower of the two
  const revisedOffer = Math.max(0, Math.min(reducedPrice, profitCeiling));
  const binding: Negotiation["binding"] =
    mp.feasible && profitCeiling < reducedPrice ? "profit" : totalReduction > 0 ? "defects" : "asking";

  const m = calc({ ...deal, purchase: revisedOffer });
  return {
    asking,
    reductions,
    totalReduction,
    reducedPrice,
    maxProfitPrice: mp.price,
    maxFeasible: mp.feasible,
    revisedOffer,
    binding,
    discount: asking > 0 ? (asking - revisedOffer) / asking : 0,
    capAtOffer: m.capOnCost,
    dscrAtOffer: m.dscr,
    targetCap,
  };
}
